import React, { useEffect, useState, useRef, useCallback } from "react";
import SockJS from "sockjs-client";
import { Stomp } from "@stomp/stompjs";
import style from "css/queue.module.css";
import { useDispatch } from "react-redux";
import { closeAlert, openAlert } from "../../redux/slices/alertSlice";

// onSuccess: 로그인 성공 시 부모에게 데이터 전달 및 알림 요청
// onClose: 취소 또는 실패 시 모달 닫기
const SignInModal = ({
  isOverlay,
  logo,
  userId,
  ticketNumber,
  onSuccess,
  onClose,
}) => {
  const dispatch = useDispatch();
  const rawServerUrl =
    process.env.REACT_APP_SERVER_URL || "http://localhost:8080";
  const serverUrl = rawServerUrl.endsWith("/")
    ? rawServerUrl.slice(0, -1)
    : rawServerUrl;

  const [remaining, setRemaining] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progressPercent, setProgressPercent] = useState(0);

  const stompClientRef = useRef(null);
  const maxProcessedRef = useRef(0);
  const checkIntervalRef = useRef(null);
  const wsConnectedRef = useRef(false);

  const handleResult = useCallback(
    (data) => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }

      if (data.status === "SUCCESS") {
        onSuccess(data);
      } else if (data.status === "FAIL") {
        const failMsg = data.message || "입력하신 정보를 다시 확인해주세요.";
        const obj = {
          title: "알림",
          content: (
            <>
              <p>{failMsg}</p>
            </>
          ),
          submitEventHandler: () => {
            dispatch(closeAlert());
            onClose();
          },
        };
        dispatch(openAlert(obj));
      } else if (data.status === "ERROR") {
        const obj = {
          title: "알림",
          content: (
            <>
              <p>오류가 발생했습니다.</p>
              <p>관리자에게 문의 바랍니다.</p>
            </>
          ),
          submitEventHandler: () => {
            dispatch(closeAlert());
            onClose();
          },
        };
        dispatch(openAlert(obj));
        onClose();
      }
    },
    [dispatch, onClose, onSuccess],
  );

  const onCheckStatus = useCallback(() => {
    fetch(`${serverUrl}/user/status/${userId}`)
      .then((res) => res.json())
      .then((res) => {
        const data = res.data || res;

        if (data.status === "SUCCESS" || data.status === "FAIL") {
          handleResult(data);
        }
      })
      .catch((err) => console.error("Status Check Error", err));
  }, [handleResult, serverUrl, userId]);

  const updateQueueDisplay = useCallback(
    (totalProcessed) => {
      if (totalProcessed < maxProcessedRef.current) return;
      maxProcessedRef.current = totalProcessed;

      const currentRemaining = ticketNumber - totalProcessed;

      if (currentRemaining <= 0) {
        setIsProcessing(true);
        setProgressPercent(100);

        // WebSocket 메시지 유실 대비 fallback 폴링 (WebSocket 연결 여부 무관)
        if (!checkIntervalRef.current) {
          onCheckStatus();
          checkIntervalRef.current = setInterval(onCheckStatus, 3000);
        }
      } else {
        setRemaining(currentRemaining);

        // 퍼센트 계산: (전체 - 남은 인원) / 전체 * 100
        // ticketNumber가 0일 경우 방어 코드
        const percent =
          ticketNumber > 0
            ? ((ticketNumber - currentRemaining) / ticketNumber) * 100
            : 0;
        setProgressPercent(Math.max(5, percent)); // 최소 5%는 보이게 설정
      }
    },
    [onCheckStatus, ticketNumber],
  );

  const connectLoginSocket = useCallback(() => {
    const socket = new SockJS(serverUrl + "/ws-signup");
    const client = Stomp.over(socket);

    client.debug = () => {};

    stompClientRef.current = client;

    client.connect(
      {},
      () => {
        wsConnectedRef.current = true;
        onCheckStatus();

        client.subscribe("/topic/auth/status/" + userId, (res) => {
          try {
            const data = JSON.parse(res.body);
            handleResult(data);
          } catch (e) {
            console.error("JSON Parse Error", e);
          }
        });

        client.subscribe("/topic/auth/processed", (res) => {
          updateQueueDisplay(parseInt(res.body));
        });

        client.subscribe("/topic/system/reset", () => {
          window.location.reload();
        });
      },
      (err) => {
        console.error("Socket Connect Error", err);
        wsConnectedRef.current = false;
        // WebSocket 실패 시 fallback 폴링 시작
        if (!checkIntervalRef.current) {
          checkIntervalRef.current = setInterval(onCheckStatus, 3000);
        }
      },
    );
  }, [handleResult, onCheckStatus, serverUrl, updateQueueDisplay, userId]);

  useEffect(() => {
    connectLoginSocket();

    return () => {
      if (stompClientRef.current) {
        try {
          stompClientRef.current.disconnect();
        } catch (e) {}
      }
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
    };
  }, [connectLoginSocket]);

  return (
    <section className={isOverlay && `${style.overlay}`}>
      <div className={`${style.modal}`}>
        {logo && <section className={`${style.logo_section}`}>{logo}</section>}

        <section className={`${style.counter_section}`}>
          <span className={`${style.counter_label}`}>나의 대기 순서</span>
          <span className={`${style.counter_number}`}>
            {isProcessing ? "0" : remaining !== null ? remaining : "..."}
          </span>

          <div className={`${style.counter_progress_bar_bg}`}>
            <div
              className={`${style.progress_bar}`}
              role="progressbar"
              style={{ width: `${progressPercent}%` }}
            ></div>
          </div>

          <article className={`${style.counter_subtext}`}>
            <p>현재 접속 인원이 많아 대기 중입니다.</p>
            <p>잠시만 기다려주시면 라이선스 신청 페이지로 연결됩니다.</p>
          </article>
        </section>

        <section className={`${style.description_section}`}>
          <div className={`${style.description_item}`}>
            <div className={`${style.description_title}`}>
              <img src="/img/common/info-icon-black.png" alt="" />
              <p>안내사항</p>
            </div>
            <div className={`${style.description_content}`}>
              <p>새로고침 하거나 재접속 하시면</p>
              <p>대기순서가 초기화되어 대기 시간이 더 길어집니다.</p>
            </div>
          </div>
          <div className={`${style.contact_item}`}>
            <p>문의</p>
            <div className={`${style.contact_info}`}>
              <img src="/img/common/mail-icon.png" alt="" />
              <p>신촌캠퍼스: digital@yonsei.ac.kr</p>
            </div>
            <div className={`${style.contact_info}`}>
              <img src="/img/common/mail-icon.png" alt="" />
              <p>미래캠퍼스: @yonsei.ac.kr</p>
            </div>
          </div>
        </section>
      </div>
    </section>
  );
};

export default SignInModal;
